import axios from "axios";

const authHeaders = (accessToken) => ({
    access_token: accessToken,
    Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
});

/**
 * Server-side checks before CardConnectPay (executable path, header, etc.).
 * @param {string} headerRowId - OM transaction header ROWID (GUID string)
 * @param {string} accessToken
 * @returns {Promise<{ success: boolean, message?: string, cardConnectPath?: string }>}
 */
export async function verifyCardConnectPay(headerRowId, accessToken) {
    if (!headerRowId || headerRowId === "00000000-0000-0000-0000-000000000000") {
        return { success: false, message: "Please SAVE HEADER Record Before Proceeding." };
    }
    try {
        const { data } = await axios.get(
            `${window._env_.REACT_APP_APIS}/om/SalesOrderHeaderOperations/VerifyCardConnectPay`,
            {
                params: { headerRowId },
                headers: authHeaders(accessToken),
            }
        );
        return {
            success: data.success === true,
            message: data.message,
            cardConnectPath: data.cardConnectPath,
        };
    } catch (error) {
        const d = error.response?.data;
        const msg =
            (d && (d.message || (typeof d === "string" ? d : ""))) ||
            error.message ||
            "Verification failed.";
        return { success: false, message: msg };
    }
}

/**
 * Amount for Card Connect popup: ISNULL(dbo.FNOM_CALCULATETRANSTOTALS(omHeaderRowId, 'OutARBalanceDue'), 0).
 * @param {string} omHeaderRowId - OMTRANSACTIONHEADER.ROWID (GUID string)
 * @param {string} accessToken
 * @returns {Promise<{ success: boolean, amount: number, message?: string }>}
 */
export async function getFnomOutArBalanceDue(omHeaderRowId, accessToken) {
    if (!omHeaderRowId || omHeaderRowId === "00000000-0000-0000-0000-000000000000") {
        return { success: false, amount: 0, message: "Invalid header." };
    }
    try {
        const { data } = await axios.get(
            `${window._env_.REACT_APP_APIS}/om/SaleOrder/GetFnomOutArBalanceDue`,
            {
                params: { omHeaderRowId },
                headers: authHeaders(accessToken),
            }
        );
        const amount = data?.amount != null ? Number(data.amount) : 0;
        return { success: data?.success !== false, amount };
    } catch (error) {
        const msg =
            error.response?.data?.message ||
            (typeof error.response?.data === "string" ? error.response.data : "") ||
            error.message ||
            "Failed to load AR balance due.";
        return { success: false, amount: 0, message: msg };
    }
}

/**
 * Runs verify then FNOM amount load (used before opening Card Connect Pay popup).
 * @returns {Promise<{ success: boolean, message?: string, amount?: number, cardConnectPath?: string }>}
 */
export async function prepareCardConnectPayOpen(headerRowId, accessToken) {
    const verify = await verifyCardConnectPay(headerRowId, accessToken);
    if (!verify.success) {
        return { success: false, message: verify.message };
    }
    const fnom = await getFnomOutArBalanceDue(headerRowId, accessToken);
    if (!fnom.success) {
        return { success: false, message: fnom.message };
    }
    return {
        success: true,
        amount: fnom.amount,
        cardConnectPath: verify.cardConnectPath,
    };
}

/**
 * Delphi opCardConnectVoidClick: header exists + OM Setup executable path (no pay type/stage rules).
 */
export async function verifyCardConnectVoidRefund(headerRowId, accessToken) {
    if (!headerRowId || headerRowId === "00000000-0000-0000-0000-000000000000") {
        return { success: false, message: "Please SAVE HEADER Record Before Proceeding." };
    }
    try {
        const { data } = await axios.get(
            `${window._env_.REACT_APP_APIS}/om/SalesOrderHeaderOperations/VerifyCardConnectVoidRefund`,
            {
                params: { headerRowId },
                headers: authHeaders(accessToken),
            }
        );
        return {
            success: data.success === true,
            message: data.message,
            cardConnectPath: data.cardConnectPath,
        };
    } catch (error) {
        const d = error.response?.data;
        const msg =
            (d && (d.message || (typeof d === "string" ? d : ""))) ||
            error.message ||
            "Verification failed.";
        return { success: false, message: msg };
    }
}

/**
 * Delphi opCardConnectVoidClick: launch Card Connect with -method voidrefund.
 * @returns {Promise<{ success: boolean, message?: string }>}
 */
export async function runCardConnectVoidRefund(headerRowId, accessToken) {
    if (!headerRowId || headerRowId === "00000000-0000-0000-0000-000000000000") {
        return { success: false, message: "Please SAVE HEADER Record Before Proceeding." };
    }
    try {
        const { data } = await axios.post(
            `${window._env_.REACT_APP_APIS}/om/SalesOrderHeaderOperations/CardConnectVoidRefund`,
            { headerRowId },
            {
                headers: {
                    "Content-Type": "application/json",
                    ...authHeaders(accessToken),
                },
            }
        );
        return {
            success: data.success === true,
            message: data.message,
        };
    } catch (error) {
        const d = error.response?.data;
        const msg =
            (d && (d.message || (typeof d === "string" ? d : ""))) ||
            error.message ||
            "Refund process failed.";
        return { success: false, message: msg };
    }
}
